export const FIELD_FONT_SIZE_OPTIONS = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 24];

/** Matches CreateEnvelope field properties font list. */
export const READONLY_EDITOR_FONT_FAMILIES = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Courier New',
  'Verdana',
  'Georgia',
  'Palatino',
  'Garamond',
  'Comic Sans MS',
  'Trebuchet MS',
];

/** Legacy Quill class fonts → inline stacks (pre style-based font). */
export const QUILL_FONT_CLASS_STACKS = {
  'ql-font-arial': 'Arial, Helvetica, sans-serif',
  'ql-font-helvetica': 'Helvetica, Arial, sans-serif',
  'ql-font-times-new-roman': '"Times New Roman", Times, serif',
  'ql-font-courier-new': '"Courier New", Courier, monospace',
  'ql-font-verdana': 'Verdana, Geneva, sans-serif',
  'ql-font-georgia': 'Georgia, serif',
  'ql-font-palatino': 'Palatino, "Palatino Linotype", serif',
  'ql-font-garamond': 'Garamond, serif',
  'ql-font-comic-sans-ms': '"Comic Sans MS", cursive, sans-serif',
  'ql-font-trebuchet-ms': '"Trebuchet MS", Helvetica, sans-serif',
};

const READONLY_HTML_MEASURE_PROBE_ID = 'volksign-readonly-html-measure';

export const hasHtmlMarkup = (value) => /<[^>]+>/.test(String(value || ''));

export const stripHtmlForMeasure = (value) =>
  String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

export const sanitizeReadOnlyValueHtml = (html) => {
  const s = String(html || '').trim();
  if (!s) return '';
  return s
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
};

/** Quill default highlight uses ql-bg-* classes that only render inside .ql-editor. */
export const QUILL_BACKGROUND_CLASS_COLORS = {
  'ql-bg-black': 'rgb(0, 0, 0)',
  'ql-bg-red': 'rgb(230, 0, 0)',
  'ql-bg-orange': 'rgb(255, 153, 0)',
  'ql-bg-yellow': 'rgb(255, 255, 0)',
  'ql-bg-green': 'rgb(0, 138, 0)',
  'ql-bg-blue': 'rgb(0, 102, 204)',
  'ql-bg-purple': 'rgb(153, 51, 255)',
};

const hasQuillBackgroundClass = (el) => {
  if (!el?.classList) return false;
  return Object.keys(QUILL_BACKGROUND_CLASS_COLORS).some((className) => el.classList.contains(className));
};

const materializeQuillBackgroundClasses = (root) => {
  if (!root) return;
  root.querySelectorAll('span').forEach((span) => {
    const bgClass = Object.keys(QUILL_BACKGROUND_CLASS_COLORS).find((className) =>
      span.classList.contains(className)
    );
    if (!bgClass) return;
    if (!span.style.backgroundColor) {
      span.style.backgroundColor = QUILL_BACKGROUND_CLASS_COLORS[bgClass];
    }
    span.classList.remove(bgClass);
  });
};

const materializeQuillFontClasses = (root) => {
  if (!root) return;
  root.querySelectorAll('span').forEach((span) => {
    const fontClass = Object.keys(QUILL_FONT_CLASS_STACKS).find((className) =>
      span.classList.contains(className)
    );
    if (!fontClass) return;
    if (!span.style.fontFamily) {
      span.style.fontFamily = QUILL_FONT_CLASS_STACKS[fontClass];
    }
    span.classList.remove(fontClass);
  });
};

const isHighlightSpan = (el) => {
  if (!el || el.tagName !== 'SPAN') return false;
  if (hasQuillBackgroundClass(el)) return true;
  if (!el.style) return false;
  const bg = el.style.backgroundColor;
  if (bg && bg !== 'transparent') return true;
  const background = el.style.background;
  if (background && background !== 'transparent' && !/^none$/i.test(String(background).trim())) {
    return true;
  }
  return spanHasOnlyStyle(el, ['background-color'])
    || spanHasOnlyStyle(el, ['background-color', 'color'])
    || spanHasOnlyStyle(el, ['background'])
    || spanHasOnlyStyle(el, ['background', 'color']);
};

const stripEditorInlineNoise = (el) => {
  if (!el?.style) return;
  el.style.removeProperty('background');
  el.style.removeProperty('background-color');
  el.style.removeProperty('margin');
  el.style.removeProperty('margin-top');
  el.style.removeProperty('margin-bottom');
  el.style.removeProperty('padding');
  el.style.removeProperty('padding-top');
  el.style.removeProperty('padding-bottom');
};

const unwrapElement = (el) => {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
};

const isElementEmpty = (el) => {
  if (!el) return true;
  const text = String(el.textContent || '').replace(/\u00a0/g, ' ').trim();
  if (text) return false;
  return !el.querySelector('br,img,svg');
};

const spanHasOnlyStyle = (span, allowedProps) => {
  if (!span?.style) return false;
  const active = [];
  for (let i = 0; i < span.style.length; i += 1) {
    const prop = span.style[i];
    if (span.style.getPropertyValue(prop)) active.push(prop);
  }
  if (active.length === 0) return allowedProps.length === 0;
  return active.length === allowedProps.length && active.every((p) => allowedProps.includes(p));
};

const flattenRedundantSpans = (root) => {
  let changed = true;
  while (changed) {
    changed = false;
    const spans = Array.from(root.querySelectorAll('span'));
    for (let i = 0; i < spans.length; i += 1) {
      const span = spans[i];
      if (span.className) continue;
      if (span.childNodes.length !== 1) continue;
      const only = span.firstChild;
      if (only?.nodeType !== 1 || only.tagName !== 'SPAN' || only.className) continue;
      const colorOnly =
        spanHasOnlyStyle(span, ['color']) && spanHasOnlyStyle(only, ['color']);
      const styleless = spanHasOnlyStyle(span, []) && spanHasOnlyStyle(only, []);
      if (colorOnly || styleless) {
        unwrapElement(span);
        changed = true;
      }
    }
  }
};

const convertHeadingToBlock = (heading) => {
  const div = document.createElement('div');
  div.className = 'readonly-block';
  const level = parseInt(heading.tagName.slice(1), 10) || 2;
  const headingSize = Math.max(12, 20 - level * 2);
  if (heading.style.fontSize) {
    div.style.fontSize = heading.style.fontSize;
  } else {
    div.style.fontSize = `${headingSize}px`;
  }
  div.style.fontWeight = heading.style.fontWeight || 'bold';
  div.style.lineHeight = '1.2';
  div.style.margin = '0';
  div.style.padding = '0';
  while (heading.firstChild) {
    div.appendChild(heading.firstChild);
  }
  return div;
};

/** Clean pasted/web HTML: drop empty blocks, flatten span nesting, tame headings. */
export const normalizeReadOnlyEditorHtml = (html) => {
  const safe = sanitizeReadOnlyValueHtml(html);
  if (!safe) return '';
  if (typeof document === 'undefined') return safe;

  const root = document.createElement('div');
  root.innerHTML = safe;

  root.querySelectorAll('script,style,iframe,object,meta,link').forEach((el) => el.remove());

  materializeQuillBackgroundClasses(root);
  materializeQuillFontClasses(root);

  for (let pass = 0; pass < 8; pass += 1) {
    const headings = root.querySelectorAll('h1,h2,h3,h4,h5,h6');
    if (!headings.length) break;
    headings.forEach((heading) => {
      const div = convertHeadingToBlock(heading);
      if (isElementEmpty(div)) {
        heading.remove();
        return;
      }
      heading.replaceWith(div);
    });
  }

  root.querySelectorAll('p,div').forEach((block) => {
    if (block === root) return;
    stripEditorInlineNoise(block);
    block.style.lineHeight = block.style.lineHeight || '1.2';
    if (isElementEmpty(block)) {
      block.remove();
    }
  });

  root.querySelectorAll('[style]').forEach((el) => {
    if (isHighlightSpan(el)) return;
    stripEditorInlineNoise(el);
  });

  flattenRedundantSpans(root);

  root.querySelectorAll('p, .readonly-block').forEach((block) => {
    if (block === root) return;
    let align = '';
    if (block.classList.contains('ql-align-center')) align = 'center';
    else if (block.classList.contains('ql-align-right')) align = 'right';
    else if (block.classList.contains('ql-align-justify')) align = 'justify';
    if (align && !block.style.textAlign) {
      block.style.textAlign = align;
    }
  });

  for (let pass = 0; pass < 3; pass += 1) {
    root.querySelectorAll('span,div,p').forEach((el) => {
      if (el === root) return;
      if (isElementEmpty(el)) {
        el.remove();
      }
    });
  }

  return root.innerHTML.trim();
};

export const prepareReadOnlyValueHtml = (html) =>
  normalizeReadOnlyEditorHtml(sanitizeReadOnlyValueHtml(html));

/** HTML for canvas + editor: valueHtml, or field_value when API sends markup. */
export const getReadOnlyFieldValueHtml = (field) => {
  if (!field) return '';
  if (field.valueHtml && hasHtmlMarkup(field.valueHtml)) {
    return String(field.valueHtml);
  }
  const fv = field.field_value;
  if (fv != null && hasHtmlMarkup(fv)) return String(fv);
  if (field.value != null && hasHtmlMarkup(field.value)) return String(field.value);
  return '';
};

const TYPOGRAPHY_FIELD_TYPES = new Set([
  'dateSigned',
  'date',
  'name',
  'email',
  'company',
  'title',
  'text',
  'number',
]);

export function fieldUsesTypographyFieldValue(type) {
  return TYPOGRAPHY_FIELD_TYPES.has(String(type || '').trim());
}

function escapeHtmlForFieldValue(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Single source of truth: persisted field_value IS the on-canvas display div. */
const READONLY_FIELD_VALUE_WRAPPER_CLASS = 'field-readonly-value-display';
/** Legacy persisted wrapper class; recognized for back-compat on load. */
const READONLY_FIELD_VALUE_WRAPPER_LEGACY_CLASS = 'field-value-typography';

function isReadOnlyFieldValueWrapper(el) {
  return Boolean(
    el
    && el.tagName === 'DIV'
    && (el.classList.contains(READONLY_FIELD_VALUE_WRAPPER_CLASS)
      || el.classList.contains(READONLY_FIELD_VALUE_WRAPPER_LEGACY_CLASS))
  );
}

/** Build the field display div (field-readonly-value-display) from field-level typography. */
export function wrapReadOnlyFieldTypographyHtml(innerHtml, field = {}) {
  const inner = String(innerHtml || '').trim();
  if (!inner) return '';
  const fontFamily = field.fontFamily ?? field.font_family ?? 'Arial';
  const sizePx = Math.max(2, parseInt(String(field.fontSize ?? field.font_size ?? 12), 10) || 12);
  const fontColor = field.fontColor ?? field.font_color ?? '#000000';
  const fontWeight = field.fontWeight ?? field.font_weight ?? 'normal';
  const fontStyle = field.fontStyle ?? field.font_style ?? 'normal';
  const textDecoration = field.textDecoration ?? field.text_decoration ?? 'none';
  const textAlign = field.textAlign ?? field.text_align ?? 'left';
  const style =
    `white-space: pre-wrap; overflow: visible; width: 100%; height: 100%; display: block; `
    + `font-family: ${fontFamily}; font-size: ${sizePx}px; color: ${fontColor}; `
    + `font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; `
    + `text-align: ${textAlign}; line-height: 1.2;`;
  return `<div class="${READONLY_FIELD_VALUE_WRAPPER_CLASS}" style="${style}">${inner}</div>`;
}

/**
 * True when the HTML string is already our current canonical field-value wrapper div.
 * Legacy-class wrappers intentionally return false so they re-wrap (and migrate) on save.
 */
function htmlIsCanonicalReadOnlyFieldValueWrapper(html) {
  const safe = sanitizeReadOnlyValueHtml(html);
  if (!safe || typeof document === 'undefined') return false;
  const root = document.createElement('div');
  root.innerHTML = safe;
  const wrapper = root.firstElementChild;
  return Boolean(
    root.childNodes.length === 1
    && wrapper
    && wrapper.tagName === 'DIV'
    && wrapper.classList.contains(READONLY_FIELD_VALUE_WRAPPER_CLASS)
  );
}

/** Remove only our own field-level wrapper div so inner content is not double-wrapped. */
export function unwrapReadOnlyFieldValueWrapper(html) {
  const safe = sanitizeReadOnlyValueHtml(html);
  if (!safe || typeof document === 'undefined') return safe || '';
  const root = document.createElement('div');
  root.innerHTML = safe;
  const wrapper = root.firstElementChild;
  if (wrapper && root.childNodes.length === 1 && isReadOnlyFieldValueWrapper(wrapper)) {
    return wrapper.innerHTML.trim();
  }
  return safe;
}

/** Build field_value for API: inner field HTML wrapped in the field-level typography div. */
export function buildReadOnlyFieldValueForApi(field) {
  if (!field) return null;
  const type = field.type ?? field.field_type;
  if (!fieldUsesTypographyFieldValue(type)) return null;

  const html = getReadOnlyFieldValueHtml(field);
  // Idempotent: when the value is already our canonical wrapper (e.g. a converted
  // envelope row re-processed on save), copy it verbatim. Re-wrapping here would
  // reset the field-level typography to defaults because the row no longer carries
  // fontSize/fontColor.
  if (html && hasHtmlMarkup(html) && htmlIsCanonicalReadOnlyFieldValueWrapper(html)) {
    return prepareReadOnlyValueHtml(html);
  }
  const plain = String(field.value ?? '').trim();
  let inner = '';
  if (html && hasHtmlMarkup(html)) {
    inner = prepareReadOnlyValueHtml(unwrapReadOnlyFieldValueWrapper(html));
  } else if (plain) {
    inner = escapeHtmlForFieldValue(plain).replace(/\r?\n/g, '<br/>');
  } else {
    return '';
  }
  if (!inner.trim()) return '';
  return wrapReadOnlyFieldTypographyHtml(inner, field);
}

/** Parse field-level typography saved on the wrapper div when loading from API. */
export function getFieldTypographyFromApiValue(field) {
  const type = field?.type ?? field?.field_type;
  if (!fieldUsesTypographyFieldValue(type)) return {};
  const html =
    field?.field_value != null && hasHtmlMarkup(field.field_value)
      ? String(field.field_value)
      : '';
  if (!html || typeof document === 'undefined') return {};

  const root = document.createElement('div');
  root.innerHTML = sanitizeReadOnlyValueHtml(html);
  const wrapper = root.firstElementChild;
  const out = {};
  if (isReadOnlyFieldValueWrapper(wrapper)) {
    const cs = wrapper.style;
    if (cs.fontFamily) {
      out.fontFamily = cs.fontFamily.split(',')[0].replace(/['"]/g, '').trim();
    }
    if (cs.fontSize) {
      const px = parseInt(cs.fontSize, 10);
      if (!Number.isNaN(px)) out.fontSize = String(px);
    }
    if (cs.color) out.fontColor = cs.color;
    if (cs.fontWeight) out.fontWeight = cs.fontWeight;
    if (cs.fontStyle) out.fontStyle = cs.fontStyle;
    if (cs.textDecoration) out.textDecoration = cs.textDecoration;
    if (cs.textAlign) out.textAlign = normalizeTextAlign(cs.textAlign, 'left');
    return out;
  }

  const parsed = resolveReadOnlyFieldPresentation({}, html);
  return {
    fontFamily: parsed.fontFamily === 'inherit' ? 'Arial' : parsed.fontFamily,
    fontSize: String(parsed.fontSize || '12'),
    fontColor: parsed.fontColor === 'inherit' ? '#000000' : parsed.fontColor,
    textAlign: parsed.textAlign || 'left',
  };
}

export const measureReadOnlyHtmlBlockHeight = ({ html, contentWidth, baseStyle = null }) => {
  if (typeof document === 'undefined') return 0;
  const width = Math.max(1, Number(contentWidth) || 1);
  let el = document.getElementById(READONLY_HTML_MEASURE_PROBE_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = READONLY_HTML_MEASURE_PROBE_ID;
    el.setAttribute('aria-hidden', 'true');
    el.className = 'field-readonly-value-display';
    Object.assign(el.style, {
      position: 'absolute',
      visibility: 'hidden',
      pointerEvents: 'none',
      left: '-9999px',
      top: '0',
      margin: '0',
      border: '0',
      boxSizing: 'border-box',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      lineHeight: '1.2',
    });
    document.body.appendChild(el);
  }
  el.style.width = `${width}px`;
  if (baseStyle) {
    const sizePx = Math.max(2, parseInt(String(baseStyle.fontSize ?? '12'), 10) || 12);
    el.style.fontFamily = baseStyle.fontFamily || 'Arial';
    el.style.fontSize = `${sizePx}px`;
    el.style.fontWeight = baseStyle.fontWeight || 'normal';
    el.style.fontStyle = baseStyle.fontStyle || 'normal';
    el.style.textDecoration = baseStyle.textDecoration || 'none';
    el.style.textAlign = baseStyle.textAlign || 'left';
    el.style.color = baseStyle.fontColor || '#000000';
  } else {
    el.style.removeProperty('font-family');
    el.style.removeProperty('font-size');
    el.style.removeProperty('font-weight');
    el.style.removeProperty('font-style');
    el.style.removeProperty('text-decoration');
    el.style.removeProperty('text-align');
    el.style.removeProperty('color');
  }
  el.innerHTML = prepareReadOnlyValueHtml(html);
  return el.scrollHeight || 0;
};

export const saveReadOnlyEditorSelection = (editorEl) => {
  if (!editorEl || typeof window === 'undefined') return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editorEl.contains(range.commonAncestorContainer)) return null;
  if (range.collapsed && !String(range.toString()).length) {
    return range.cloneRange();
  }
  return range.cloneRange();
};

export const restoreReadOnlyEditorSelection = (editorEl, savedRange) => {
  if (!editorEl || typeof window === 'undefined') return false;
  editorEl.focus();
  const sel = window.getSelection();
  if (!sel) return false;
  if (savedRange) {
    sel.removeAllRanges();
    sel.addRange(savedRange);
    return true;
  }
  return false;
};

const getRangeInEditor = (editorEl, savedRange) => {
  if (!editorEl) return null;
  restoreReadOnlyEditorSelection(editorEl, savedRange);
  const sel = window.getSelection();
  if (!sel) return null;
  if (sel.rangeCount === 0 || sel.isCollapsed) {
    const range = document.createRange();
    range.selectNodeContents(editorEl);
    sel.removeAllRanges();
    sel.addRange(range);
    return range;
  }
  const range = sel.getRangeAt(0);
  if (!editorEl.contains(range.commonAncestorContainer)) {
    const fallback = document.createRange();
    fallback.selectNodeContents(editorEl);
    sel.removeAllRanges();
    sel.addRange(fallback);
    return fallback;
  }
  return range;
};

const enableCssExecCommand = () => {
  try {
    document.execCommand('styleWithCSS', false, true);
  } catch {
    // ignore
  }
};

const rangeContentsToHtml = (range) => {
  const tmp = document.createElement('div');
  tmp.appendChild(range.cloneContents());
  return tmp.innerHTML;
};

const wrapRangeInSpan = (range, styleDeclarations) => {
  const span = document.createElement('span');
  Object.assign(span.style, styleDeclarations);
  const fragment = range.extractContents();
  span.appendChild(fragment);
  range.insertNode(span);
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    const after = document.createRange();
    after.selectNodeContents(span);
    after.collapse(false);
    sel.addRange(after);
  }
  return span;
};

export const applyReadOnlyRichCommand = (editorEl, command, value = null, savedRange = null) => {
  if (!editorEl || typeof document === 'undefined') return;
  getRangeInEditor(editorEl, savedRange);
  enableCssExecCommand();
  try {
    document.execCommand(command, false, value);
  } catch {
    // ignore unsupported commands
  }
};

export const applyReadOnlyFontColor = (editorEl, color, savedRange = null) => {
  if (!editorEl || typeof document === 'undefined' || !color) return;
  getRangeInEditor(editorEl, savedRange);
  enableCssExecCommand();
  try {
    document.execCommand('foreColor', false, color);
  } catch {
    // ignore
  }
};

export const applyReadOnlyInlineStyles = (editorEl, styleDeclarations = {}, savedRange = null) => {
  if (!editorEl || typeof document === 'undefined') return;

  if (styleDeclarations.color) {
    applyReadOnlyFontColor(editorEl, styleDeclarations.color, savedRange);
    const { color, ...rest } = styleDeclarations;
    if (Object.keys(rest).length === 0) return;
    return applyReadOnlyInlineStyles(editorEl, rest, savedRange);
  }

  const range = getRangeInEditor(editorEl, savedRange);
  if (!range) return;

  const styles = {};
  if (styleDeclarations.fontSize) styles.fontSize = styleDeclarations.fontSize;
  if (styleDeclarations.fontFamily) styles.fontFamily = styleDeclarations.fontFamily;
  if (Object.keys(styles).length === 0) return;

  try {
    wrapRangeInSpan(range, styles);
  } catch {
    const html = rangeContentsToHtml(range);
    range.deleteContents();
    const span = document.createElement('span');
    Object.assign(span.style, styles);
    span.innerHTML = html || '';
    range.insertNode(span);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      const after = document.createRange();
      after.selectNodeContents(span);
      after.collapse(false);
      sel.addRange(after);
    }
  }
};

/** Normalize editor DOM in place (paste/format); preserves focus when possible. */
export const normalizeReadOnlyEditorElement = (editorEl) => {
  if (!editorEl || typeof document === 'undefined') return '';
  const saved = saveReadOnlyEditorSelection(editorEl);
  const normalized = normalizeReadOnlyEditorHtml(editorEl.innerHTML);
  if (normalized !== editorEl.innerHTML) {
    editorEl.innerHTML = normalized;
    restoreReadOnlyEditorSelection(editorEl, saved);
  }
  return editorEl.innerHTML;
};

export const applyReadOnlyTextAlign = (editorEl, align, savedRange = null) => {
  if (!editorEl) return;
  editorEl.style.textAlign = align || 'left';
  const cmd = align === 'center' ? 'justifyCenter' : align === 'right' ? 'justifyRight' : 'justifyLeft';
  applyReadOnlyRichCommand(editorEl, cmd, null, savedRange);
};

const normalizeTextAlign = (value, fallback = 'left') => {
  const align = String(value || '').trim().toLowerCase();
  if (align === 'center' || align === 'right' || align === 'justify') return align;
  if (align === 'start') return 'left';
  if (align === 'end') return 'right';
  return fallback;
};

const getReadOnlyBlockAlignment = (el) => {
  if (!el) return 'left';
  if (el.classList.contains('ql-align-center')) return 'center';
  if (el.classList.contains('ql-align-right')) return 'right';
  if (el.classList.contains('ql-align-justify')) return 'justify';
  const inline = el.style?.textAlign;
  if (inline) return normalizeTextAlign(inline, 'left');
  return 'left';
};

const readTextAlignFromHtmlTree = (root) => {
  if (!root) return '';
  if (root.querySelector('.ql-align-center')) return 'center';
  if (root.querySelector('.ql-align-right')) return 'right';
  if (root.querySelector('.ql-align-justify')) return 'justify';

  const styledNodes = root.querySelectorAll('[style]');
  for (let i = 0; i < styledNodes.length; i += 1) {
    const align = styledNodes[i].style?.textAlign;
    if (align) return align;
  }
  return '';
};

function parseFontSizePx(value) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  const pxMatch = raw.match(/^(\d+(?:\.\d+)?)\s*px$/i);
  if (pxMatch) return Math.round(parseFloat(pxMatch[1]));
  const num = parseInt(raw, 10);
  return Number.isNaN(num) ? null : num;
}

function collectInlineFontSizesPx(root) {
  const sizes = new Set();
  if (!root) return sizes;
  root.querySelectorAll('[style]').forEach((el) => {
    const px = parseFontSizePx(el.style?.fontSize);
    if (px != null) sizes.add(px);
  });
  return sizes;
}

/** True when rich HTML uses more than one inline font-size. */
export const readOnlyHtmlHasMixedFontSize = (html) => {
  if (!hasHtmlMarkup(html) || typeof document === 'undefined') return false;
  const root = document.createElement('div');
  root.innerHTML = sanitizeReadOnlyValueHtml(html);
  return collectInlineFontSizesPx(root).size > 1;
};

/**
 * Remove inline typography that matches field-level defaults so parent/canvas styles apply.
 * Keeps inline styles when the HTML has mixed font sizes (selection formatting).
 */
export const stripUniformReadOnlyInlineTypography = (html, field = {}) => {
  if (!html || typeof document === 'undefined') return html || '';
  const safe = sanitizeReadOnlyValueHtml(html);
  if (!safe) return '';

  if (readOnlyHtmlHasMixedFontSize(safe)) {
    return prepareReadOnlyValueHtml(safe);
  }

  const root = document.createElement('div');
  root.innerHTML = safe;

  root.querySelectorAll('[style]').forEach((el) => {
    if (el.style?.fontSize) {
      el.style.removeProperty('font-size');
    }
    if (el.style?.fontFamily) {
      el.style.removeProperty('font-family');
    }
    if (el.style?.color) {
      el.style.removeProperty('color');
    }
    if (!el.getAttribute('style')?.trim()) {
      el.removeAttribute('style');
    }
  });

  return prepareReadOnlyValueHtml(root.innerHTML);
};

/** True when rich HTML has paragraphs/blocks with different alignments. */
export const readOnlyHtmlHasMixedAlignment = (html) => {
  if (!hasHtmlMarkup(html) || typeof document === 'undefined') return false;
  const root = document.createElement('div');
  root.innerHTML = sanitizeReadOnlyValueHtml(html);
  const blocks = Array.from(root.querySelectorAll('p, .readonly-block')).filter(
    (el) => !isElementEmpty(el)
  );
  if (blocks.length <= 1) return false;
  const aligns = new Set(blocks.map((el) => getReadOnlyBlockAlignment(el)));
  return aligns.size > 1;
};

export const parseReadOnlyHtmlToolbarDefaults = (html) => {
  const defaults = {
    fontFamily: 'Arial',
    fontSize: '12',
    fontColor: '#000000',
    textAlign: 'left',
  };
  if (!html || typeof document === 'undefined') return defaults;
  const wrap = document.createElement('div');
  wrap.innerHTML = sanitizeReadOnlyValueHtml(html);
  const styled = wrap.querySelector('[style]') || wrap.firstElementChild;
  if (styled) {
    const cs = styled.style;
    if (cs.fontFamily) {
      defaults.fontFamily = cs.fontFamily.split(',')[0].replace(/['"]/g, '').trim() || defaults.fontFamily;
    }
    if (cs.fontSize) {
      const px = parseInt(cs.fontSize, 10);
      if (!Number.isNaN(px)) defaults.fontSize = String(px);
    }
    if (cs.color) defaults.fontColor = cs.color;
    if (cs.textAlign) defaults.textAlign = normalizeTextAlign(cs.textAlign, defaults.textAlign);
  }
  const treeAlign = readTextAlignFromHtmlTree(wrap);
  if (treeAlign) defaults.textAlign = normalizeTextAlign(treeAlign, defaults.textAlign);
  return defaults;
};

/** Merge API field settings with rich HTML defaults for display (signing canvas, etc.). */
export const resolveReadOnlyFieldPresentation = (field, htmlValue) => {
  const html = String(htmlValue || '');
  const fromWrapper =
    html && hasHtmlMarkup(html)
      ? getFieldTypographyFromApiValue({
          ...field,
          field_value: html,
          field_type: field?.field_type ?? field?.type,
        })
      : {};
  const fromHtml = hasHtmlMarkup(html) ? parseReadOnlyHtmlToolbarDefaults(html) : null;
  return {
    fontFamily:
      field?.font_family
      || field?.fontFamily
      || fromWrapper?.fontFamily
      || fromHtml?.fontFamily
      || 'inherit',
    fontSize:
      field?.font_size
      || field?.fontSize
      || fromWrapper?.fontSize
      || fromHtml?.fontSize
      || '12',
    fontColor:
      field?.font_color
      || field?.fontColor
      || fromWrapper?.fontColor
      || fromHtml?.fontColor
      || 'inherit',
    fontWeight: field?.font_weight || field?.fontWeight || fromWrapper?.fontWeight || 'normal',
    fontStyle: field?.font_style || field?.fontStyle || fromWrapper?.fontStyle || 'normal',
    textDecoration:
      field?.text_decoration || field?.textDecoration || fromWrapper?.textDecoration || 'none',
    textAlign: normalizeTextAlign(
      field?.text_align || field?.textAlign || fromWrapper?.textAlign || fromHtml?.textAlign,
      'left'
    ),
  };
};

/** Inline styles for the field-readonly-value-display host (matches API wrapper). */
export const readOnlyPresentationToInlineStyle = (presentation = {}) => {
  const fontFamily =
    presentation.fontFamily && presentation.fontFamily !== 'inherit'
      ? presentation.fontFamily
      : 'Arial';
  const sizePx = Math.max(2, parseInt(String(presentation.fontSize || '12'), 10) || 12);
  const fontColor =
    presentation.fontColor && presentation.fontColor !== 'inherit'
      ? presentation.fontColor
      : '#000000';
  return {
    whiteSpace: 'pre-wrap',
    overflow: 'visible',
    width: '100%',
    height: '100%',
    display: 'block',
    boxSizing: 'border-box',
    padding: '2px 4px',
    fontFamily,
    fontSize: `${sizePx}px`,
    color: fontColor,
    fontWeight: presentation.fontWeight || 'normal',
    fontStyle: presentation.fontStyle || 'normal',
    textDecoration: presentation.textDecoration || 'none',
    textAlign: presentation.textAlign || 'left',
    lineHeight: 1.2,
  };
};

export const readOnlyPresentationToFlexStyle = (textAlign) => {
  const align = normalizeTextAlign(textAlign, 'left');
  if (align === 'center') {
    return { justifyContent: 'flex-start', alignItems: 'center', textAlign: 'center' };
  }
  if (align === 'right') {
    return { justifyContent: 'flex-start', alignItems: 'flex-end', textAlign: 'right' };
  }
  if (align === 'justify') {
    return { justifyContent: 'flex-start', alignItems: 'stretch', textAlign: 'justify' };
  }
  return { justifyContent: 'flex-start', alignItems: 'flex-start', textAlign: 'left' };
};

export const readSelectionFormatState = () => {
  if (typeof document === 'undefined') {
    return { bold: false, italic: false, underline: false };
  }
  try {
    return {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
    };
  } catch {
    return { bold: false, italic: false, underline: false };
  }
};
